import type { EntityType } from "@/shared/types/entity-view";

export type HostNavigationEntityType = EntityType;

export type HostEntityNavigationMessage = {
  type: "claimmosaic:navigate";
  entityType: HostNavigationEntityType;
  entityId: number;
  source: "entity-card";
};

export interface HostNavigationRuntime {
  isEmbedded: boolean;
  locationSearch: string;
  referrer: string;
  postMessageToParent: (
    message: HostEntityNavigationMessage,
    targetOrigin: string
  ) => void;
}

const isHostNavigationEntityType = (
  value: unknown
): value is HostNavigationEntityType =>
  value === "claim" ||
  value === "task" ||
  value === "contact" ||
  value === "company";

const isPositiveInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

const parseOrigin = (value: string): string | null => {
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
};

const resolveQueryHostOrigin = (locationSearch: string): string | null => {
  try {
    const hostOrigin = new URLSearchParams(locationSearch).get("hostOrigin");
    if (!hostOrigin) {
      return null;
    }
    return parseOrigin(hostOrigin);
  } catch {
    return null;
  }
};

export const resolveHostNavigationOrigin = (
  locationSearch: string,
  referrer: string
): string | null => {
  const queryOrigin = resolveQueryHostOrigin(locationSearch);
  const referrerOrigin = referrer ? parseOrigin(referrer) : null;

  if (queryOrigin && referrerOrigin && queryOrigin !== referrerOrigin) {
    return null;
  }

  return queryOrigin ?? referrerOrigin;
};

const resolveBrowserRuntime = (): HostNavigationRuntime | null => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }

  return {
    isEmbedded: window.parent !== window,
    locationSearch: window.location.search,
    referrer: document.referrer,
    postMessageToParent: (message, targetOrigin) =>
      window.parent.postMessage(message, targetOrigin)
  };
};

export function postHostEntityNavigation(
  entityType: HostNavigationEntityType,
  entityId: number,
  runtime?: HostNavigationRuntime
): boolean {
  if (!isHostNavigationEntityType(entityType) || !isPositiveInteger(entityId)) {
    return false;
  }

  const effectiveRuntime = runtime ?? resolveBrowserRuntime();
  if (!effectiveRuntime || !effectiveRuntime.isEmbedded) {
    return false;
  }

  const targetOrigin = resolveHostNavigationOrigin(
    effectiveRuntime.locationSearch,
    effectiveRuntime.referrer
  );
  if (!targetOrigin) {
    return false;
  }

  effectiveRuntime.postMessageToParent(
    {
      type: "claimmosaic:navigate",
      entityType,
      entityId,
      source: "entity-card"
    },
    targetOrigin
  );

  return true;
}
